import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

interface FormErrors {
  username?: string;
  accessKey?: string;
  submit?: string;
}

/**
 * BrowserStack connect — a device-farm api-key connector: a username plus the account access key
 * (both required by identity's `browserstack/credentials` route). No OAuth path.
 *
 * The connection is the caller's own, like every other one. It used to land on the workspace
 * service-account, so one BrowserStack login answered for everybody here; the interface stopped asking
 * for that lane, and this form's connect now writes a personal row.
 */
export function BrowserStackCredentialsForm({
  onSaved,
  onCancel,
  details,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
}): JSX.Element {
  const [username, setUsername] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();

  /** Reads the clipboard into the access-key field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setAccessKey(text.trim());
      if (errors.accessKey)
        setErrors((es) => ({ ...es, accessKey: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const trimmedUsername = username.trim();
    const trimmedKey = accessKey.trim();
    const found: FormErrors = {};
    if (!trimmedUsername) found.username = 'Username is required';
    if (!trimmedKey) found.accessKey = 'Access key is required';
    if (found.username || found.accessKey) {
      setErrors(found);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveBrowserStackCredentials(
        { username: trimmedUsername, accessKey: trimmedKey },
        workspaceId,
        details,
      );
      onSaved();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="browserstack-username" className="text-xs">
          Username
        </Label>
        <Input
          id="browserstack-username"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="BrowserStack username"
          value={username}
          onChange={(event) => {
            setUsername(event.target.value);
            if (errors.username)
              setErrors((es) => ({ ...es, username: undefined }));
          }}
          disabled={submitting}
          aria-invalid={Boolean(errors.username)}
        />
        {errors.username && (
          <p className="text-xs text-destructive">{errors.username}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="browserstack-access-key" className="text-xs">
          Access key
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="browserstack-access-key"
              type={showKey ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="BrowserStack access key"
              value={accessKey}
              onChange={(event) => {
                setAccessKey(event.target.value);
                if (errors.accessKey)
                  setErrors((es) => ({ ...es, accessKey: undefined }));
              }}
              disabled={submitting}
              aria-invalid={Boolean(errors.accessKey)}
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowKey((shown) => !shown)}
              aria-label={showKey ? 'Hide access key' : 'Show access key'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showKey ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={submitting}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.accessKey ? (
          <p className="text-xs text-destructive">{errors.accessKey}</p>
        ) : (
          <p className="text-[11px] leading-snug text-muted-foreground">
            Find your username and access key in BrowserStack under{' '}
            <span className="font-medium">Account → Settings</span>.
          </p>
        )}
      </div>

      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
